import React from "react";
import findIndex from "lodash.findindex";
import find from "lodash.find";
import styles from "./styles.module.scss";
import {
    ENTER_KEY_CODE,
    UP_ARROW_KEY_CODE,
    DOWN_ARROW_KEY_CODE,
    FIRST_LETTER_KEY_CODE,
    LAST_LETTER_KEY_CODE,
    SPACE_KEY_CODE
} from "constants/v2/KeyCodes";

const TOP_PADDING = 5;
const PLACEHOLDER_TEXT = "Select an item";
const SELECTBOX_MODIFIERS = {
    profession: "profession",
    round: "round"
};

class DropdownMenu extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            expanded: false,
            highlightedValue: "",
            blurValue: null,
            searchString: "",
            searchItems: props.items
        };

        this.selectid = `dropdown-menu-${ props.label.toLowerCase().split(" ").join("-") }`;
        this._handleKeyPress = this._handleKeyPress.bind(this);
        this._handleBlur = this._handleBlur.bind(this);
    }

    componentDidMount() {
        if (this.props.autofocus) {
            this.selectboxWrap.focus();
        }
    }

    componentDidUpdate() {
        const {
            dropdownContainer,
            highlightedElement,
            state: {
                expanded,
                highlightedValue
            }
        } = this;

        if (expanded && highlightedValue) {
            this._ensureInViewport(dropdownContainer, highlightedElement);
        }
    }

    _ensureInViewport(dropdownContainer, highlightedElement) {
        const dropdownBottomY = dropdownContainer.scrollTop + dropdownContainer.getBoundingClientRect().height;
        const selectBoxBottomY = highlightedElement.offsetTop + highlightedElement.getBoundingClientRect().height;

        if (selectBoxBottomY > dropdownBottomY) {
            dropdownContainer.scrollTop = highlightedElement.offsetTop;

            return;
        }

        if (selectBoxBottomY - TOP_PADDING <= dropdownContainer.scrollTop) {
            dropdownContainer.scrollTop = highlightedElement.offsetTop;
        }
    }

    _toggleExpanded() {
        this.setState({
            expanded: !this.state.expanded,
            searchString: "",
            searchItems: this.props.items
        });
    }

    _updateHighlightedValue(event) {
        if (event.target.tagName.toLowerCase() === "ul") {
            return;
        }

        const value = event.target.getAttribute("data-value");

        this.setState({
            highlightedValue: value
        });
    }

    /*
     * [PT] [WBF-5705] 2/20/18
     * We need to save the value that user did mouseDown on
     * because the mousedown part of the click on a dropdown value
     * causes the parent DOM element that wraps the container to
     * blur and this blur event would prevent us from getting
     * access to the click event because blur fires before
     * click. So we use mousedown instead because that
     * fires *before* blur so we can save the value the
     * user clicked and make that the selected value
     * before the dropdown loses focus. For a more detailed
     * explanation, see https://stackoverflow.com/questions/12092261/prevent-firing-the-blur-event-if-any-one-of-its-children-receives-focus
     */
    _updateBlurValue(event) {
        if (event.target.className === styles.listItem) {
            return;
        }

        const value = event.target.getAttribute("data-value");

        this.setState({
            blurValue: value
        });
    }

    _changeValue(event) {
        if (event.target.className === styles.listItem) {
            return;
        }

        const value = event.target.getAttribute("data-value");

        this.props.onValueChange(value);
        this.setState({
            expanded: false,
            blurValue: undefined,
            searchString: "",
            searchItems: this.props.items
        });
    }

    _handleEnterKeyPress(value) {
        this.props.onValueChange(value);
        this._toggleExpanded();
    }

    _handleBlur() {
        const { blurValue } = this.state;

        if (blurValue) {
            this.selectboxWrap.focus();

            return;
        }

        this.setState({
            expanded: false,
            searchString: "",
            searchItems: this.props.items
        });
    }

    _search(letter) {
        const { searchString, searchItems } = this.state;

        const newSearchString = `${ searchString }${ letter.toLowerCase() }`;
        const newSearchItems = searchItems.filter(({ key }) => key.toLowerCase().startsWith(newSearchString));

        this.setState({
            searchString: newSearchString,
            searchItems: newSearchItems
        });

        return newSearchItems[0] && newSearchItems[0].value;
    }

    _handleLetterKeyPress(letter) {
        const { highlightedValue } = this.state;
        const nextValue = this._search(letter) || highlightedValue;

        this.setState({
            highlightedValue: nextValue,
            expanded: true
        });
    }

    _handleArrowKeyPress(keyCode, activeValue) {
        const { items } = this.props;
        const startIndex = findIndex(items, (keyValuePair) => keyValuePair.value === activeValue);
        const indexDelta = keyCode === UP_ARROW_KEY_CODE ? -1 : 1;
        const nextIndex = Math.min(Math.max(startIndex + indexDelta, 0), items.length - 1);

        this.setState({
            highlightedValue: items[nextIndex].value,
            expanded: true
        });
    }

    _handleKeyPress(event) {
        const { expanded, highlightedValue } = this.state;
        const { value } = this.props;
        const { key, keyCode } = event;

        const activeValue = expanded ? (highlightedValue || "") : value;

        if (keyCode === ENTER_KEY_CODE && expanded && highlightedValue) {
            this._handleEnterKeyPress(highlightedValue);

            return;
        }

        if ((keyCode >= FIRST_LETTER_KEY_CODE && keyCode <= LAST_LETTER_KEY_CODE) || keyCode === SPACE_KEY_CODE) {
            this._handleLetterKeyPress(key);

            return;
        }

        if (keyCode === UP_ARROW_KEY_CODE || keyCode === DOWN_ARROW_KEY_CODE) {
            event.preventDefault();
            this._handleArrowKeyPress(keyCode, activeValue);

            return;
        }
    }

    _renderItems(items, activeValue) {
        return items.map(({ key, value }) => {
            const className = activeValue === value ? `${ styles.dropdownOption } ${ styles.active }` : styles.dropdownOption;

            return (
                <li
                    ref={ activeValue === value ? (el) => {
                        this.highlightedElement = el;
                    } : null }
                    className={ className }
                    data-value={ value }
                    key={ value }
                >
                    { key }
                </li>
            );
        });
    }

    render() {
        const {
            highlightedValue,
            expanded
        } = this.state;

        const {
            items,
            value,
            label,
            placeholder,
            hidden,
            error = {},
            disabled = false,
            theme
        } = this.props;

        const selectedItem = find(items, (item) => item.value === value);
        const selectedText = selectedItem ? selectedItem.key : (placeholder || PLACEHOLDER_TEXT);
        const className = `${ styles.selectbox } ${ (SELECTBOX_MODIFIERS[theme] && styles[SELECTBOX_MODIFIERS[theme]]) || "" } ${ error.message ? styles.error : "" }`;
        const showLabel = value || error.message;

        if (hidden) {
            return null;
        }

        return (
            <div className={ className }>
                { showLabel && <label selectid={ `${ this.selectid }-label` } className={ styles.label } htmlFor={ styles.selectbox }>{ error.message || label }</label> }
                <div // eslint-disable-line
                    selectid={ this.selectid }
                    ref={ (element) => {
                        this.selectboxWrap = element;
                    } }
                    className={ `${ styles.wrap } ${ disabled ? styles.disabled : "" }` }
                    tabIndex="0" // eslint-disable-line
                    onKeyDown={ !disabled ? this._handleKeyPress : () => { } }
                    onClick={ !disabled ? this._toggleExpanded.bind(this) : () => { } }
                    onBlur={ !disabled ? this._handleBlur : () => { } }
                >
                    <div className={ `${ styles.text } ${ selectedItem ? styles.selected : "" }` }>{ selectedText }</div>
                    { !disabled && <div className={ expanded ? styles.closeIcon : styles.openIcon } /> }
                </div>
                <div className={ `dropdownList ${ styles.dropdown } ${ expanded ? styles.open : styles.closed }` }>
                    <ul // eslint-disable-line
                        className={ styles.listItem }
                        ref={ (element) => this.dropdownContainer = element }
                        onClick={ (event) => this._changeValue(event) }
                        onMouseMove={ (event) => this._updateHighlightedValue(event) }
                        onMouseDown={ (event) => this._updateBlurValue(event) }
                    >
                        { this._renderItems(items, highlightedValue) }
                    </ul>
                </div>
            </div>
        );
    }
}

export default DropdownMenu;
